import {
  Appointment,
  PAYMENT_STATUS,
  RECEIPT_PAYMENT_MODE_ID,
  SUBJECT_KEYS,
} from "@elixir/types";
import { useMemo } from "react";
import { ReactComponent as ChevronDownRegularIcon } from "../../../../assets/icons/font-awesome/chevron-down-regular.svg";
import { ReactComponent as IndianRupeeSignRegularIcon } from "../../../../assets/icons/font-awesome/indian-rupee-sign-regular.svg";
import { PAY_MODES } from "../../../../constants/receipt";
import TooltipComponent from "../../../../organisms/common/tooltip-component";
import useBillingStore from "../../../../stores/billing-store/billing-store";
import useQueueStore from "../../../../stores/queue-store";
import { createNewReceipt, viewAllReceipt } from "../../../../utils/receipt";
import { getAclViewClassName, roundToTwoDecimals } from "@elixir/utils";

const ReceiptButton = ({
  className = "",
  apt,
}: {
  className?: string;
  apt: Appointment;
}) => {
  const { receipts = [], aid } = apt || {};

  const { amount, hasPendingPayment, paymode, tooltipText } = useMemo(() => {
    const paymodeSet = new Set<RECEIPT_PAYMENT_MODE_ID>();
    const amount = receipts.reduce((acc, receipt) => {
      return (
        acc +
        receipt.receipt_payment?.reduce((acc, rp) => {
          paymodeSet.add(rp.paymode);
          return acc + rp.amount;
        }, 0)
      );
    }, 0);

    const hasPendingPayment = receipts.find(
      (item) => item.payment_status !== PAYMENT_STATUS.SUCCESS
    );
    const paymodeList = Array.from(paymodeSet);

    const paymode = !paymodeList.length
      ? "Cash"
      : paymodeList.length > 1
        ? "Multiple"
        : (PAY_MODES.find((p) => p.id === paymodeList[0])?.name as string);

    const tooltipText = !receipts.length
      ? "Create Receipt"
      : receipts.length === 1
        ? `₹${amount} | ${paymode}`
        : "View All Receipts";

    return { amount, hasPendingPayment, paymode, tooltipText };
  }, [receipts]);

  const clinicId = useQueueStore((state) => state.clinicId);

  const setClinicId = useBillingStore((state) => state.setClinicId);
  const isReceiptNotCreated = receipts.length === 0;

  const handleReceiptAction = () => {
    setClinicId({ clinicId });
    if (isReceiptNotCreated) {
      createNewReceipt({ appointment: apt });
    } else if (receipts.length === 1) {
      viewAllReceipt({ appointment: apt });
    } else {
      viewAllReceipt({ appointment: apt });
    }
  };

  return (
    <TooltipComponent title={tooltipText} placement="top">
      <div
        onClick={handleReceiptAction}
        className={`border flex items-center h-32 dr-rounded-4  hover:dr-text-darwin-touch-neutral-1000  dr-text-darwin-touch-neutral-1000 ${
          isReceiptNotCreated
            ? "dr-bg-darwin-neutral-0 hover:dr-text-darwin-neutral-1000 hover:dr-bg-darwin-neutral-50 dr-text-darwin-neutral-600 dr-border-darwin-neutral-200"
            : hasPendingPayment
              ? "dr-border-darwin-touch-yellow-bg-dark hover:dr-bg-darwin-touch-yellow-bg-dark hover:dr-border-darwin-touch-yellow-primary dr-bg-darwin-touch-yellow-bg-light"
              : "dr-border-darwin-touch-green-bg-dark hover:dr-bg-darwin-touch-green-bg-dark hover:dr-border-darwin-touch-green-primary dr-bg-darwin-touch-green-bg-light"
        } dr-cursor-pointer ${className} ${getAclViewClassName(
          SUBJECT_KEYS.CAN_CREATE_RECEIPT_WITH_APT
        )}`}
      >
        <div className="dr-flex dr-items-center dr-flex-1 px-8 py-10 dr-space-x-10">
          <IndianRupeeSignRegularIcon />
          <div className="dr-flex-1">{roundToTwoDecimals(amount)}</div>
        </div>
        <div className="w-1 h-full dr-bg-doctor-darwin-neutral-100"></div>
        <div className="dr-flex items-center dr-flex-1 px-8 py-10 dr-space-x-10">
          <div className="dr-flex-1 dr-line-clamp-1 dr-w-full">{paymode}</div>
          <ChevronDownRegularIcon className="-dr-rotate-90" />
        </div>
      </div>
    </TooltipComponent>
  );
};

export default ReceiptButton;
